/**
 * Retry Manager — bounded retries with backoff for provider calls.
 *
 * LLM APIs fail transiently (rate limits, timeouts); design math doesn't.
 * So retries wrap ONLY the provider stage of the pipeline. Backoff is
 * injectable (and zero in tests); cancellation short-circuits the wait.
 */

import { CancelledError } from './cancellation';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const DEFAULT_RETRY_OPTIONS = {
  retries: 2, // attempts = retries + 1
  baseDelayMs: 500, // doubles per retry: 500, 1000, 2000…
  shouldRetry: () => true,
};

/**
 * Run `fn` with retries.
 * options = { retries, baseDelayMs, shouldRetry(error), onRetry({attempt, error, delayMs}), token }
 * Never retries a CancelledError. Throws the last error when exhausted.
 */
export const withRetry = async (fn, options = {}) => {
  const { retries, baseDelayMs, shouldRetry, onRetry, token } = {
    ...DEFAULT_RETRY_OPTIONS,
    ...options,
  };

  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    token?.throwIfCancelled();
    try {
      return await fn(attempt);
    } catch (error) {
      lastError = error;
      const outOfAttempts = attempt === retries;
      if (error instanceof CancelledError || outOfAttempts || !shouldRetry(error)) {
        throw error;
      }
      const delayMs = baseDelayMs * 2 ** attempt;
      onRetry?.({ attempt: attempt + 1, error, delayMs });
      if (delayMs > 0) await sleep(delayMs);
    }
  }
  throw lastError; // unreachable, kept for safety
};
